import { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Typography,
  TextField,
} from '@mui/material'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { lessonAssociationsAtom, treeStructureResultAtom } from './atoms'
import {
  getTabValuesByTitle,
  listTabs,
  googleUserAtom,
  GoogleLoginButton,
} from 'infrastructure/googleDrive'
import { useTreeSourceMode } from './useScopeAndSequenceUrlParams'
import {
  EXPORT_HEADERS,
  CROSSWALK_METADATA_LABEL,
} from './ExportToGoogleSheetsButton'
import { ScopeSequenceTreeStructureResponse } from 'components/contentService/ScopeSequenceTreeStructureResponse'
import { BorderedContainer } from 'components/utility/Containers'
import { TreeCreationDetails } from './TreeCreationDetails'
import {
  SpreadsheetLesson,
  SpreadsheetUnit,
  SpreadsheetImportData,
  LessonEntry,
  LessonCentricData,
  LessonAssociation,
} from 'domains/scope-and-sequence/types'
import { convertSpreadsheetToLessonCentric } from './convertFromApi'
import { CrosswalkSelector } from './CrosswalkSelector'
import { useSpreadsheetImport } from './SpreadsheetWorkflow.hooks'

function extractSpreadsheetId(urlOrId: string): string | null {
  if (!urlOrId.includes('/')) {
    return urlOrId.trim()
  }
  const match = urlOrId.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
  return match ? match[1] : null
}

function parseGradeFromTabTitle(title: string): number {
  const match = title.match(/Grade\s+(\d+)/i)
  return match ? parseInt(match[1], 10) : 0
}

function parseLessonRow(row: string[]): SpreadsheetLesson | null {
  // Column indices account for informational "Aligned Standards" column at index 3
  const treeId = row[8]?.trim()
  const versionId = row[9]?.trim()
  const nodeId = row[10]?.trim()

  if (!treeId || !versionId || !nodeId) return null

  const targetUnitStr = row[5]?.trim()
  let targetUnit: number | null = null
  if (targetUnitStr && targetUnitStr.toUpperCase() !== 'EXCLUDE') {
    const parsed = parseInt(targetUnitStr, 10)
    if (!isNaN(parsed)) targetUnit = parsed
  }

  const targetLessonNumStr = row[7]?.trim()
  let targetLessonNumber: number | null = null
  if (targetLessonNumStr) {
    const parsed = parseInt(targetLessonNumStr, 10)
    if (!isNaN(parsed) && parsed > 0) targetLessonNumber = parsed
  }

  return {
    nodePath: row[0] || '',
    lessonName: row[1] || '',
    standards: row[2] || '',
    suggestedUnits: row[4] || '',
    targetUnit,
    targetSection: row[6]?.trim() || '',
    targetLessonNumber,
    treeId,
    versionId,
    nodeId,
  }
}

function parseUnitRow(row: string[]): SpreadsheetUnit | null {
  const number = parseInt(row[0], 10)
  const name = row[1]?.trim()
  if (isNaN(number) || !name) return null
  return { number, name }
}

async function fetchSpreadsheetData(
  accessToken: string,
  spreadsheetId: string,
): Promise<SpreadsheetImportData> {
  const tabs = await listTabs(spreadsheetId, accessToken)

  // Find lessons tab
  const lessonsTab = tabs.find((t) => t.title.includes('Lessons'))
  if (!lessonsTab) {
    throw new Error('Could not find a "Lessons" tab in the spreadsheet')
  }

  // Find unit reference tab
  const unitTab = tabs.find((t) => t.title.includes('Unit Reference'))

  // Fetch lessons data
  const lessonsResponse = await getTabValuesByTitle({
    spreadsheetId,
    title: lessonsTab.title,
    accessToken,
  })

  if (!lessonsResponse.values || lessonsResponse.values.length < 2) {
    throw new Error('Spreadsheet appears to be empty or has no data rows')
  }

  // Find header row (may have metadata rows above it)
  const headerIndex = lessonsResponse.values.findIndex(
    (row) => row[0] === EXPORT_HEADERS[0],
  )
  const dataStartIndex = headerIndex >= 0 ? headerIndex + 1 : 1

  // Scan metadata rows above header for crosswalk mapping set ID
  let crosswalkMappingSetId: number | undefined
  for (let i = 0; i < (headerIndex >= 0 ? headerIndex : 0); i++) {
    const row = lessonsResponse.values[i]
    if (row[0] === CROSSWALK_METADATA_LABEL) {
      const parsed = parseInt(row[1], 10)
      if (!isNaN(parsed)) crosswalkMappingSetId = parsed
      break
    }
  }

  // Parse lessons (skip header and any metadata rows)
  const lessons: SpreadsheetLesson[] = []
  for (let i = dataStartIndex; i < lessonsResponse.values.length; i++) {
    const lesson = parseLessonRow(lessonsResponse.values[i])
    if (lesson) lessons.push(lesson)
  }

  // Parse grade from tab title
  const grade = parseGradeFromTabTitle(lessonsTab.title)

  // Fetch and parse units
  let units: SpreadsheetUnit[] = []
  if (unitTab) {
    const unitsResponse = await getTabValuesByTitle({
      spreadsheetId,
      title: unitTab.title,
      accessToken,
    })
    if (unitsResponse.values) {
      for (let i = 1; i < unitsResponse.values.length; i++) {
        const unit = parseUnitRow(unitsResponse.values[i])
        if (unit) units.push(unit)
      }
    }
  }

  // If no unit tab, derive units from lesson assignments
  if (units.length === 0) {
    const unitSet = new Set<number>()
    lessons.forEach((l) => {
      if (l.targetUnit) unitSet.add(l.targetUnit)
    })
    units = Array.from(unitSet)
      .sort((a, b) => a - b)
      .map((n) => ({ number: n, name: `Unit ${n}` }))
  }

  return { lessons, units, grade, crosswalkMappingSetId }
}

function SpreadsheetUrlInput({
  loading,
  error,
  urlInput,
  setUrlInput,
  handleFetchFromUrl,
  switchToManual,
  isAuthenticated,
}: {
  loading: boolean
  error: string | null
  urlInput: string
  setUrlInput: (v: string) => void
  handleFetchFromUrl: () => void
  switchToManual: () => void
  isAuthenticated: boolean
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#fff' }}>
      <CardContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Import from Google Spreadsheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the URL or ID of a previously exported S&S spreadsheet.
          </Typography>

          {!isAuthenticated ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Sign in with Google to access spreadsheets:
              </Typography>
              <GoogleLoginButton />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Google Sheets URL or ID"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                fullWidth
                disabled={loading}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleFetchFromUrl}
                disabled={loading || !urlInput.trim()}
              >
                {loading ? <CircularProgress size={20} /> : 'Import'}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

function SpreadsheetImportView({
  importData,
  lessonCentricData,
  assignedLessons,
  poolLessons,
  lessonAssociations,
  spreadsheetId,
  fetchAndImport,
  clearImport,
  switchToSpreadsheetPending,
  switchToManual,
}: {
  importData: SpreadsheetImportData
  lessonCentricData: LessonCentricData | null
  assignedLessons: LessonEntry[]
  poolLessons: LessonEntry[]
  lessonAssociations: Record<string, LessonAssociation>
  spreadsheetId: string | undefined
  fetchAndImport: (
    sheetId: string,
    fetcher: (
      accessToken: string,
      spreadsheetId: string,
    ) => Promise<SpreadsheetImportData>,
  ) => Promise<boolean>
  clearImport: () => void
  switchToSpreadsheetPending: () => void
  switchToManual: () => void
}) {
  return (
    <Box>
      <Card
        variant="outlined"
        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
      >
        <CardContent>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h6">
                Spreadsheet Import: Grade {importData.grade}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assignedLessons.length} assigned, {poolLessons.length} in pool
                ({lessonCentricData?.lessons.length ?? 0} total lessons)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  spreadsheetId &&
                  fetchAndImport(spreadsheetId, fetchSpreadsheetData)
                }
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  clearImport()
                  switchToSpreadsheetPending()
                }}
              >
                Change Sheet
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {lessonCentricData && (
        <BorderedContainer>
          <ScopeSequenceTreeStructureResponse
            lessons={assignedLessons}
            unitCount={lessonCentricData.units.length}
            lessonAssociations={lessonAssociations}
            extraColumns={[
              {
                title: 'Section',
                width: 100,
                rowContent({ tree_id, version_id, node_id }) {
                  const key = `${tree_id}:${version_id}:${node_id}`
                  return lessonAssociations[key]?.targetSection ?? ''
                },
              },
              {
                title: 'Lesson #',
                width: 80,
                rowContent({ tree_id, version_id, node_id }) {
                  const key = `${tree_id}:${version_id}:${node_id}`
                  return lessonAssociations[key]?.targetLessonNumber ?? ''
                },
              },
            ]}
            toolBarItems={[]}
          />
        </BorderedContainer>
      )}

      <CrosswalkSelector />

      <Card
        variant="outlined"
        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
      >
        <CardContent>
          <TreeCreationDetails />
        </CardContent>
      </Card>
    </Box>
  )
}

export function SpreadsheetWorkflow() {
  const guser = useAtomValue(googleUserAtom)
  const {
    spreadsheetId,
    switchToManual,
    switchToSpreadsheet,
    switchToSpreadsheetPending,
  } = useTreeSourceMode()

  const setTreeStructureResult = useSetAtom(treeStructureResultAtom)
  const lessonAssociations = useAtomValue(lessonAssociationsAtom)

  const {
    loading,
    error,
    importData,
    fetchAndImport,
    clearError,
    clearImport,
    setError,
  } = useSpreadsheetImport()

  const [urlInput, setUrlInput] = useState('')

  const lessonCentricData = useMemo(() => {
    if (!importData) return null
    return convertSpreadsheetToLessonCentric(importData)
  }, [importData])

  // Auto-fetch on mount if we have a spreadsheet ID
  useEffect(() => {
    if (spreadsheetId && guser.credential?.accessToken && !importData) {
      fetchAndImport(spreadsheetId, fetchSpreadsheetData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsheetId, guser.credential?.accessToken])

  const handleFetchFromUrl = async () => {
    const id = extractSpreadsheetId(urlInput)
    if (!id) {
      setError('Invalid Google Sheets URL or ID')
      return
    }
    const success = await fetchAndImport(id, fetchSpreadsheetData)
    if (success && id !== spreadsheetId) {
      switchToSpreadsheet(id)
    }
  }

  // Recalculate tree structure result when associations change
  useEffect(() => {
    if (!importData) return

    // Rebuild import data with updated associations
    const updatedLessons = importData.lessons.map((lesson) => {
      const key = `${lesson.treeId}:${lesson.versionId}:${lesson.nodeId}`
      const assoc = lessonAssociations[key]
      if (assoc) {
        return { ...lesson, targetUnit: assoc.targetUnit }
      }
      return lesson
    })

    const updatedData = { ...importData, lessons: updatedLessons }
    const converted = convertSpreadsheetToLessonCentric(updatedData)
    setTreeStructureResult(converted)
  }, [lessonAssociations, importData, setTreeStructureResult])

  // Partition lessons into assigned and pool based on associations
  const { assignedLessons, poolLessons } = useMemo(() => {
    if (!lessonCentricData) return { assignedLessons: [], poolLessons: [] }

    const assigned: LessonEntry[] = []
    const pool: LessonEntry[] = []

    lessonCentricData.lessons.forEach((lesson) => {
      const assoc = lessonAssociations[lesson.key]
      if (assoc?.targetUnit != null) {
        assigned.push(lesson)
      } else {
        pool.push(lesson)
      }
    })

    assigned.sort((a, b) => {
      const aAssoc = lessonAssociations[a.key]
      const bAssoc = lessonAssociations[b.key]
      const unitDiff = (aAssoc?.targetUnit ?? 0) - (bAssoc?.targetUnit ?? 0)
      if (unitDiff !== 0) return unitDiff
      const sectionDiff = (aAssoc?.targetSection ?? '').localeCompare(
        bAssoc?.targetSection ?? '',
        undefined,
        { numeric: true },
      )
      if (sectionDiff !== 0) return sectionDiff
      return (
        (aAssoc?.targetLessonNumber ?? 0) - (bAssoc?.targetLessonNumber ?? 0)
      )
    })

    return { assignedLessons: assigned, poolLessons: pool }
  }, [lessonCentricData, lessonAssociations])

  const isAuthenticated = !!guser.credential?.accessToken

  // Show Google Auth button if we have a spreadsheet ID but user is not authenticated
  if (spreadsheetId && !isAuthenticated) {
    return (
      <Card
        variant="outlined"
        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
      >
        <CardContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Sign in to Access Spreadsheet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sign in with Google to load the spreadsheet.
            </Typography>
            <GoogleLoginButton />
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Show URL input if no spreadsheet loaded
  if (!spreadsheetId && !importData) {
    return (
      <SpreadsheetUrlInput
        loading={loading}
        error={error}
        urlInput={urlInput}
        setUrlInput={setUrlInput}
        handleFetchFromUrl={handleFetchFromUrl}
        switchToManual={switchToManual}
        isAuthenticated={isAuthenticated}
      />
    )
  }

  // Loading state
  if (loading) {
    return (
      <Card
        variant="outlined"
        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
      >
        <CardContent>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading spreadsheet data...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card
        variant="outlined"
        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
      >
        <CardContent>
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="outlined" onClick={clearError}>
              Try Again
            </Button>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (!importData) return null

  return (
    <SpreadsheetImportView
      importData={importData}
      lessonCentricData={lessonCentricData}
      assignedLessons={assignedLessons}
      poolLessons={poolLessons}
      lessonAssociations={lessonAssociations}
      spreadsheetId={spreadsheetId}
      fetchAndImport={fetchAndImport}
      clearImport={clearImport}
      switchToSpreadsheetPending={switchToSpreadsheetPending}
      switchToManual={switchToManual}
    />
  )
}
